/* eslint-disable */
/*
 * eAuditor formula question evaluator (v1) - for the web app and the React Native app.
 *
 * Evaluates the STORED formula (question.Formula.Expr) so the result can be shown live while the
 * auditor types. The server recalculates on every save and its value is the one that is stored,
 * scored and printed - this file only has to show the same number in the meantime.
 *
 *   import { evaluateFormula, getFormulaInputs } from './formula-eval';
 *   const r = evaluateFormula(q.Formula.Expr, guid => answerTextByGuid[guid], q.Formula.Decimals);
 *   // r.text = "12.50" ("" when blank), r.errorCode = DIV0 | NUM | BLANK_INPUT | INVALID, r.blankInputs = [guid...]
 *
 * Inputs are referenced as {q:<template question GUID>} = AuditDataQuestion.QuestionOriginalId.
 * Resolve each GUID inside the formula's own section copy first (repeated sections), then the
 * original section (IsCopiedSection = false). Pass the answer TEXT exactly as stored
 * (SingleResponse.Response); null/undefined/"" = no answer. Inputs in a section marked N/A = no answer.
 *
 * Mirrors Li.CQATS.Audit.Library/Formula/FormulaEngine.cs. Both run formula-cases.json
 * (node formula-eval.test.js). Do not change rounding / parsing rules here without changing the C#.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.EAuditorFormula = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var MAX_DECIMALS = 6;
  var FUNCS = {
    SUM: { min: 1, max: 255, agg: true },
    AVERAGE: { min: 1, max: 255, agg: true },
    MIN: { min: 1, max: 255, agg: true },
    MAX: { min: 1, max: 255, agg: true },
    ROUND: { min: 2, max: 2, agg: false },
    ROUNDUP: { min: 2, max: 2, agg: false },
    ROUNDDOWN: { min: 2, max: 2, agg: false },
    ABS: { min: 1, max: 1, agg: false }
  };

  function FormulaError(message) { this.message = message; }

  // ---------- tokenizer (stored form only) ----------
  function tokenize(text) {
    var toks = [], i = 0, n = text.length;
    while (i < n && /\s/.test(text[i])) i++;
    if (i < n && text[i] === '=') i++;
    while (i < n) {
      var c = text[i];
      if (/\s/.test(c)) { i++; continue; }
      var start = i;
      if (/[0-9]/.test(c) || (c === '.' && i + 1 < n && /[0-9]/.test(text[i + 1]))) {
        while (i < n && /[0-9]/.test(text[i])) i++;
        if (i < n && text[i] === '.') { i++; while (i < n && /[0-9]/.test(text[i])) i++; }
        if (i < n && (text[i] === 'e' || text[i] === 'E')) {
          var save = i; i++;
          if (i < n && (text[i] === '+' || text[i] === '-')) i++;
          if (i < n && /[0-9]/.test(text[i])) { while (i < n && /[0-9]/.test(text[i])) i++; } else i = save;
        }
        var v = Number(text.substring(start, i));
        if (!isFinite(v)) throw new FormulaError('Invalid number');
        toks.push({ k: 'num', v: v }); continue;
      }
      if (c === '{') {
        var close = text.indexOf('}', i);
        if (close < 0 || text.substr(i, 3).toLowerCase() !== '{q:') throw new FormulaError('Invalid question reference');
        var guid = text.substring(i + 3, close).trim();
        if (!/^[0-9A-Za-z\-]{1,64}$/.test(guid)) throw new FormulaError('Invalid question reference');
        toks.push({ k: 'ref', v: guid }); i = close + 1; continue;
      }
      if (/[A-Za-z_]/.test(c)) {
        while (i < n && /[A-Za-z0-9_]/.test(text[i])) i++;
        var word = text.substring(start, i).toUpperCase();
        var j = i; while (j < n && /\s/.test(text[j])) j++;
        if (j < n && text[j] === '(') { toks.push({ k: 'name', v: word }); continue; }
        throw new FormulaError("'" + word + "' is not supported");
      }
      if ('+-*/^'.indexOf(c) >= 0) { toks.push({ k: 'op', v: c }); i++; continue; }
      if (c === '(') { toks.push({ k: '(' }); i++; continue; }
      if (c === ')') { toks.push({ k: ')' }); i++; continue; }
      if (c === ',' || c === ';') { toks.push({ k: ',' }); i++; continue; }
      if (c === '%') { toks.push({ k: '%' }); i++; continue; }
      throw new FormulaError("Unexpected character '" + c + "'");
    }
    toks.push({ k: 'end' });
    return toks;
  }

  // ---------- parser (Excel precedence: + - < * / < ^ left-assoc < unary minus < postfix %) ----------
  function parse(text) {
    var t = tokenize(text || ''), p = 0, depth = 0;
    function peek() { return t[p]; }
    function next() { return t[p++]; }
    function isOp(tok, chars) { return tok.k === 'op' && chars.indexOf(tok.v) >= 0; }
    function enter() { if (++depth > 100) throw new FormulaError('Too deeply nested'); }

    function add() {
      var left = mul();
      while (isOp(peek(), '+-')) { var op = next().v; left = { t: 'bin', op: op, l: left, r: mul() }; }
      return left;
    }
    function mul() {
      var left = pow();
      while (isOp(peek(), '*/')) { var op = next().v; left = { t: 'bin', op: op, l: left, r: pow() }; }
      return left;
    }
    function pow() {
      var left = unary();
      while (isOp(peek(), '^')) { next(); left = { t: 'bin', op: '^', l: left, r: unary() }; }
      return left;
    }
    function unary() {
      if (isOp(peek(), '-')) { next(); enter(); var o = unary(); depth--; return { t: 'neg', x: o }; }
      if (isOp(peek(), '+')) { next(); return unary(); }
      return postfix();
    }
    function postfix() {
      var node = primary();
      while (peek().k === '%') { next(); node = { t: 'bin', op: '/', l: node, r: { t: 'num', v: 100 } }; }
      return node;
    }
    function primary() {
      var tok = peek();
      if (tok.k === 'num') { next(); return { t: 'num', v: tok.v }; }
      if (tok.k === 'ref') { next(); return { t: 'ref', g: tok.v }; }
      if (tok.k === 'name') {
        next();
        var spec = FUNCS[tok.v];
        if (!spec) throw new FormulaError('Function ' + tok.v + ' is not supported');
        if (peek().k !== '(') throw new FormulaError("Expected '('");
        next(); enter();
        var args = [];
        if (peek().k !== ')') {
          for (;;) { args.push(add()); if (peek().k === ',') { next(); continue; } break; }
        }
        if (peek().k !== ')') throw new FormulaError("Missing ')'");
        next(); depth--;
        if (args.length < spec.min || args.length > spec.max) throw new FormulaError(tok.v + ': wrong number of arguments');
        return { t: 'fn', name: tok.v, args: args };
      }
      if (tok.k === '(') {
        next(); enter();
        var e = add();
        if (peek().k !== ')') throw new FormulaError("Missing ')'");
        next(); depth--;
        return e;
      }
      throw new FormulaError('Unexpected end of formula');
    }

    if (peek().k === 'end') throw new FormulaError('The formula is empty');
    var tree = add();
    if (peek().k !== 'end') throw new FormulaError('Unexpected token');
    return tree;
  }

  // ---------- numbers ----------
  // 15 significant digits - removes binary noise. Same as C# double.Parse(x.ToString("G15")).
  function n15(x) {
    if (x === 0 || !isFinite(x)) return x;
    return Number(x.toPrecision(15));
  }

  // Excel ROUND (mode 0, half away from zero) / ROUNDUP (1) / ROUNDDOWN (2)
  function roundExcel(x, digits, mode) {
    if (x === 0 || !isFinite(x)) return x;
    var p = Math.pow(10, digits);
    var v = n15(x);
    var s = n15(Math.abs(v) * p);
    var r = mode === 0 ? Math.floor(s + 0.5) : mode === 1 ? Math.ceil(s) : Math.floor(s);
    return n15((v < 0 ? -1 : 1) * r / p);
  }

  // Answer text -> number. "." first; "," as the decimal mark when there is no "." (Spanish UIs).
  // Exactly 3 digits after a single comma ("1,234") is ambiguous (thousands or decimals) - not guessed.
  var NUM_RX = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/;
  function parseInput(text) {
    if (text === null || text === undefined) return null;
    var s = String(text).trim();
    if (s === '') return null;
    if (NUM_RX.test(s)) { var d = Number(s); return isFinite(d) ? d : null; }
    if (s.indexOf(',') >= 0 && s.indexOf('.') < 0 && s.length - s.indexOf(',') - 1 !== 3) {
      var alt = s.replace(/,/g, '.');
      if (alt.split('.').length === 2 && NUM_RX.test(alt)) { var d2 = Number(alt); return isFinite(d2) ? d2 : null; }
    }
    return null;
  }

  // ---------- evaluator ----------
  var BLANK = { blank: true };
  function err(code) { return { err: code }; }

  function ev(node, lookup, blanks) {
    switch (node.t) {
      case 'num': return { v: node.v };
      case 'ref': {
        var d = parseInput(lookup(node.g));
        if (d === null) { if (blanks.indexOf(node.g) < 0) blanks.push(node.g); return BLANK; }
        return { v: d };
      }
      case 'neg': {
        var o = ev(node.x, lookup, blanks);
        if (o.err || o.blank) return o;
        return { v: -o.v };
      }
      case 'bin': {
        var l = ev(node.l, lookup, blanks), r = ev(node.r, lookup, blanks);
        if (l.err) return l;
        if (r.err) return r;
        if (l.blank || r.blank) return BLANK;
        var x;
        switch (node.op) {
          case '+': x = l.v + r.v; break;
          case '-': x = l.v - r.v; break;
          case '*': x = l.v * r.v; break;
          case '/': if (r.v === 0) return err('DIV0'); x = l.v / r.v; break;
          default:
            if (l.v === 0 && r.v === 0) return err('NUM'); // Excel: 0^0 = #NUM!
            x = Math.pow(l.v, r.v);
        }
        if (!isFinite(x)) return err('NUM');
        return { v: x };
      }
      case 'fn': {
        var vals = node.args.map(function (a) { return ev(a, lookup, blanks); });
        for (var i = 0; i < vals.length; i++) if (vals[i].err) return vals[i];
        var spec = FUNCS[node.name];
        if (spec.agg) {
          var nums = vals.filter(function (v) { return !v.blank; }).map(function (v) { return v.v; });
          if (nums.length === 0) return BLANK;
          var y;
          if (node.name === 'SUM') y = nums.reduce(function (a, b) { return a + b; }, 0);
          else if (node.name === 'AVERAGE') y = nums.reduce(function (a, b) { return a + b; }, 0) / nums.length;
          else if (node.name === 'MIN') y = Math.min.apply(null, nums);
          else y = Math.max.apply(null, nums);
          if (!isFinite(y)) return err('NUM');
          return { v: y };
        }
        for (var k = 0; k < vals.length; k++) if (vals[k].blank) return BLANK;
        if (node.name === 'ABS') return { v: Math.abs(vals[0].v) };
        var nd = Math.max(-15, Math.min(15, Math.trunc(vals[1].v)));
        var mode = node.name === 'ROUND' ? 0 : node.name === 'ROUNDUP' ? 1 : 2;
        var z = roundExcel(vals[0].v, nd, mode);
        if (!isFinite(z)) return err('NUM');
        return { v: z };
      }
    }
    return err('INVALID');
  }

  var MESSAGES = { DIV0: "Can't divide by 0.", NUM: 'The result is not a valid number.', BLANK_INPUT: 'Waiting for input values.' };

  /**
   * @param {string} expr      stored formula (question.Formula.Expr)
   * @param {function} lookup  guid -> stored answer text (string | null)
   * @param {number} decimals  question.Formula.Decimals (0-6)
   * @returns {{value: number|null, text: string, errorCode: string|null, message: string|null, blankInputs: string[]}}
   */
  function evaluateFormula(expr, lookup, decimals) {
    var out = { value: null, text: '', errorCode: null, message: null, blankInputs: [] };
    var tree;
    try { tree = parse(expr); } catch (e) { out.errorCode = 'INVALID'; out.message = e.message; return out; }
    decimals = Math.max(0, Math.min(MAX_DECIMALS, decimals | 0));
    var r = ev(tree, lookup || function () { return null; }, out.blankInputs);
    if (r.err) { out.errorCode = r.err; out.message = MESSAGES[r.err] || 'The formula could not be calculated.'; return out; }
    if (r.blank) { out.errorCode = 'BLANK_INPUT'; out.message = MESSAGES.BLANK_INPUT; return out; }
    var v = roundExcel(r.v, decimals, 0);
    if (!isFinite(v) || Math.abs(v) >= 1e15) { out.errorCode = 'NUM'; out.message = 'The result is too large.'; return out; }
    if (v === 0) v = 0; // no "-0"
    out.value = v;
    out.text = v.toFixed(decimals);
    return out;
  }

  /** GUIDs used by a stored formula (to know which formulas to refresh when an answer changes). */
  function getFormulaInputs(expr) {
    var refs = [];
    (function walk(n) {
      if (!n) return;
      if (n.t === 'ref') { if (refs.indexOf(n.g) < 0) refs.push(n.g); }
      else if (n.t === 'neg') walk(n.x);
      else if (n.t === 'bin') { walk(n.l); walk(n.r); }
      else if (n.t === 'fn') n.args.forEach(walk);
    })(parse(expr));
    return refs;
  }

  /** "12.50" + suffix ("%" sticks to the number, other units get a space). "" stays "". */
  function formatFormulaDisplay(text, suffix) {
    if (!text) return '';
    if (!suffix || !String(suffix).trim()) return text;
    suffix = String(suffix).trim();
    return suffix === '%' ? text + '%' : text + ' ' + suffix;
  }

  return {
    evaluateFormula: evaluateFormula,
    getFormulaInputs: getFormulaInputs,
    formatFormulaDisplay: formatFormulaDisplay,
    parseInput: parseInput,
    roundExcel: roundExcel
  };
});
